function fixAccessibility() {
    wpToolbarIssues();
    checkLinkTarget();
    googleTranslatePreloader();
}

/**
 * @function checkLinkTarget
 * @author Dreamers of Day
 * @description
 * Iterates over all anchor (`<a>`) elements with the `js-icon-link` class and a valid `href`.
 * If the link points to an external domain, it appends the screen-reader text '(opens in a new tab)'
 * for accessibility purposes.
 *
 * External URLs are determined by comparing the origin of the `href` to the current window's origin.
 * This improves accessibility by informing screen readers that the link opens in a new tab.
 *
 * Any errors during `URL` construction (e.g., malformed `href`) are caught and silently ignored.
 *
 * @returns {void}
 */
function checkLinkTarget() {
    // Define the accessibility notice text to be appended if the link opens in a new tab
    const accessibilityText = '(opens in a new tab)';

    // Select all anchor elements with the 'js-icon-link' class that also have an 'href' attribute
    document.querySelectorAll('a.js-icon-link[href]').forEach((linkElement) => {
        // Extract the 'href' value directly from the DOM node
        const hrefValue = linkElement.getAttribute('href');

        // Attempt to construct a URL object from the 'href' and the current origin
        try {
            // If 'hrefValue' is a relative URL, the base will be set to window.location.origin
            const linkURL = new URL(hrefValue, window.location.origin);

            // Determine whether the link is external by comparing the origin of the link to the current page origin
            const isExternalLink = linkURL.origin !== window.location.origin;

            // If the link is internal (i.e., same origin), skip any further processing for this link
            if (!isExternalLink) {
                return;
            }

            // Attempt to locate the element intended for screen reader text (usually a visually hidden span)
            const srOnlyElement = linkElement.querySelector('.sr-only');

            // If a screen reader element exists and does not already include the accessibility text, append it
            if (
                srOnlyElement &&
                typeof srOnlyElement.textContent === 'string' &&
                !srOnlyElement.textContent.includes(accessibilityText)
            ) {
                // Trim any existing whitespace before appending the accessibility text
                srOnlyElement.textContent = `${srOnlyElement.textContent.trim()} ${accessibilityText}`;
            }
        } catch (error) {
            // Silent fail: the URL constructor may throw an error for malformed hrefs
            // This ensures that one bad link doesn't break the loop
            // It is intentional that we do not log or rethrow the error to avoid polluting the console
        }
    });
}

/**
 * Issue: Content is not within a landmark element
 * Explanation: We should avoid having not a HTML5 sectioning element without a role attribute.
 * Therefore, we need to add a certain role attribute value to the WP toolbar element
 * Element: #wpadminbar
 */
function wpToolbarIssues() {
    const wpToolbar = document.getElementById('wpadminbar');
    if (wpToolbar) {
        wpToolbar.setAttribute('role', 'contentinfo');
    }
}

/**
 * Add for svg preloader role attribute.
 */
function googleTranslatePreloader() {
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (
                    node.nodeType === 1 &&
                    node.parentElement === document.body
                ) {
                    node.querySelectorAll('svg').forEach((svg) => {
                        svg.setAttribute('role', 'presentation');
                    });
                }
            });
        });
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true,
    });
}

export default fixAccessibility;
